import React from "react";

import getMenuItems from "api/requests/getMenuItems";
import { Action, Category } from "constants/events";
import { SnackBarMessages, useSnackBar } from "context/SnackBarContext";
import { DispensaryV3 } from "custom-types/DispensariesV3";
import MenuItemFilterSection from "custom-types/MenuItemFilterSection";
import useDomainCountryCode from "hooks/useDomainCountryCode";
import { useEventTracker } from "hooks/useEventTracker";
import useFilters from "hooks/useFilters";
import { getDispensaryUrl } from "utils/dispensaryUrls";
import logError from "utils/logError";

import Button from "components/botanic/Button";
import Carousel from "components/botanic/Carousel";
import MenuItemCard from "components/botanic/MenuItemCard";
import { getMenuItemIndicator } from "components/Dispensary/MenuCard/MenuCard";
import { MenuItemCardActions } from "components/Shared/MenuItemCarousel/MenuItemCardActions";
import { MenuCardSkeleton } from "components/Shared/Skeletons";
import TrackImpression from "components/TrackImpression";

import DeliveryCarouselHeader from "./DeliveryCarouselHeader";
import MenuItemCount from "./MenuItemCount";
import SortBy from "./SortBy";

const DeliveryCarousels = ({
  className,
  dispensaries,
  initialMenuItems,
  isDeliveryAvailable = true,
  onChangeSort,
  selectedSort,
  referringPage,
}: {
  className?: string;
  dispensaries: Array<DispensaryV3>;
  isDeliveryAvailable?: boolean;
  initialMenuItems?: {
    // the any[] here is expected to be an array of menu items
    // however the types need some tidying up
    // https://leafly.atlassian.net/browse/BRND-656
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
    [dispensarySlug: string]: any[];
  };
  onChangeSort: (newVal?: string) => void;
  selectedSort?: string;
  referringPage?: string;
}) => {
  const dispoChunkSize = 7;
  const { addSnackBarItem } = useSnackBar();
  const [menuItems, setMenuItems] = React.useState(initialMenuItems || {});
  const [menuItemCount, setMenuItemCount] = React.useState<number>();
  const [selectedCategory, setSelectedCategory] = React.useState<string | null>(
    null,
  );
  const countryCode = useDomainCountryCode();
  const [visibleDispensaries, setVisibleDispensaries] = React.useState(
    dispensaries.slice(0, dispoChunkSize),
  );
  const { publishEvent } = useEventTracker();
  const { toggleFilter, selectFilterValues } = useFilters();

  React.useEffect(() => {
    // if the root set of dipsos changes, reset to page 1 and reload
    setVisibleDispensaries(dispensaries.slice(0, dispoChunkSize));
    setSelectedCategory(null);
    loadDispoData({
      category: null,
      dispos: dispensaries.slice(0, dispoChunkSize),
      overwrite: true,
    });
  }, [dispensaries]);

  const loadDispoData = ({
    dispos,
    category,
    overwrite = false,
  }: {
    dispos: DispensaryV3[];
    category: string | null;
    overwrite?: boolean; // force overwrite dispo data, vs lazily keeping data we already loaded
  }) => {
    let disposToLoad: string[] = [];
    const newMenuItems = {};
    if (overwrite) {
      disposToLoad = dispos.map((d) => d.slug);
      setMenuItems({});
    } else {
      dispos.forEach((dispo) => {
        if (menuItems[dispo.slug]) {
          // Reuse data we already have instead of reloading.
          // @ts-ignore (fix me please, do not replicate)
          newMenuItems[dispo.slug] = menuItems[dispo.slug];
        } else {
          disposToLoad.push(dispo.slug);
        }
      });
    }

    Promise.all(
      disposToLoad.map((slug) =>
        wrapMenuReq(
          slug,
          getMenuItems(
            slug,
            category
              ? {
                  product_category: category,
                }
              : {},
          ),
        ),
      ),
    )
      .then((promiseResults) => {
        promiseResults.forEach((res) => {
          // we could be setting an empty array if no menu.  This is OK.
          // @ts-ignore (fix me please, do not replicate)
          newMenuItems[res.slug] = res.data?.menuItems || [];
        });
        setMenuItems({ ...menuItems, ...newMenuItems });

        setMenuItemCount(
          promiseResults
            .map((res) => res?.data?.totalItems || 0)
            .reduce((total, item) => total + item, 0),
        );
      })
      .catch(() => {
        logError(
          "Error processing Dispensary Data in DeliveryMenuCarousels useEffect",
        );
      });
  };

  /*
      1) if there are no menu item results, then we won't be able to get the dispo slug
         from the first menuItem result on getMenu.  This wraps the slug into the result.
      2) if it takes too long to get all the dispo results, the Promise.all is crapping out
         and we get zero carousels.  So set a time limit and let each promise time out
         (returning [] in that case) individually, before the whole thing can croak.
  */
  const wrapMenuReq = (
    slug: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
    request: any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  ): Promise<{ slug: string; data: any }> => {
    return Promise.race([
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
      new Promise<{ slug: string; data: any }>((resolve) => {
        // @ts-ignore (fix me please, do not replicate)
        request.then((data) => {
          resolve({
            data,
            slug,
          });
        });
      }),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
      new Promise<{ slug: string; data: any }>((resolve) => {
        setTimeout(() => {
          resolve({ data: [], slug });
        }, 4500);
      }),
    ]);
  };

  const loadMoreDispos = () => {
    loadDispoData({
      category: selectedCategory,
      dispos: dispensaries.slice(
        0,
        visibleDispensaries.length + dispoChunkSize,
      ),
    });
    setVisibleDispensaries(
      dispensaries.slice(0, visibleDispensaries.length + dispoChunkSize),
    );
  };

  if (!visibleDispensaries?.length) {
    return null;
  }

  const getMerchandisingCampaignId = (dispensary: DispensaryV3) => {
    const dpp = dispensary?.merchandisedCampaigns?.filter((campaign) => {
      return campaign.unitName === "delivery-platinum-placement";
    });

    return dpp?.[0]?.id;
  };

  const headerMessaging = isDeliveryAvailable
    ? "Dispensary delivery menus"
    : "Try ordering for in-store pickup.";

  return (
    <div className={className}>
      <div className="flex justify-between">
        <MenuItemCount
          menuItemsCount={menuItemCount}
          selectedCategory={selectedCategory}
        />
        {isDeliveryAvailable && (
          <SortBy
            selectedSort={selectedSort}
            onChange={onChangeSort}
            isDeliveryAvailable={isDeliveryAvailable}
          />
        )}
      </div>
      <hr className="border-light-grey -mx-lg mt-sm mb-lg md:hidden" />
      {referringPage !== "shop" && <h3>{headerMessaging}</h3>}
      {visibleDispensaries
        // undefined menu items -> still loading (show skelements)
        // empty [] menu items -> loaded, but no matching items (hide)
        .filter(
          (d) =>
            typeof menuItems[d.slug] === "undefined" ||
            menuItems[d.slug]?.length,
        )
        .map((dispo, index) => (
          <TrackImpression
            key={dispo.id}
            trackingFunction={() =>
              publishEvent({
                action: Action.impression,
                carouselNumber: index + 1,
                category: Category.deliveryGateFlow,
                dispensaryId: dispo.id,
                label: "retailer carousel",
                merchandisingCampaignId: getMerchandisingCampaignId(dispo),
              })
            }
          >
            <Carousel
              cardsPerSlide={3}
              title={
                <TrackImpression
                  trackingFunction={() =>
                    publishEvent({
                      action: Action.impression,
                      category: Category.deliveryGateFlow,
                      dispensaryId: dispo.id,
                      label: "retailer see all link",
                      merchandisingCampaignId:
                        getMerchandisingCampaignId(dispo),
                    })
                  }
                >
                  <DeliveryCarouselHeader
                    dispensary={dispo}
                    isDeliveryAvailable={isDeliveryAvailable}
                    merchandisingCampaignId={getMerchandisingCampaignId(dispo)}
                  />
                </TrackImpression>
              }
              seeMoreText={`See all ${
                isDeliveryAvailable ? "delivery" : ""
              } products`}
              seeMoreHref={getDispensaryUrl({
                countryCode,
                slug: dispo.slug,
                tab: "menu",
              })}
              seeMoreOnClick={() => {
                publishEvent({
                  action: Action.click,
                  category: Category.deliveryGateFlow,
                  dispensaryId: dispo.id,
                  label: "retailer see all link",
                  merchandisingCampaignId: getMerchandisingCampaignId(dispo),
                });
              }}
              className="mb-lg lg:mb-none"
            >
              {menuItems[dispo.slug]?.length
                ? menuItems[dispo.slug].map((menuItem) => (
                    <TrackImpression
                      key={menuItem.id}
                      className="h-full"
                      trackingFunction={() =>
                        publishEvent({
                          action: Action.impression,
                          brandId: menuItem.brand?.id,
                          category: Category.deliveryGateFlow,
                          dispensaryId: menuItem.dispensary.id,
                          label: "product card",
                          menuItemId: menuItem.menuItemId,
                          merchandisingCampaignId:
                            getMerchandisingCampaignId(dispo),
                          productId: menuItem.product?.id,
                          strainId: menuItem.strain?.id,
                        })
                      }
                    >
                      <MenuItemCard
                        handleCardClick={() =>
                          publishEvent({
                            action: Action.click,
                            brandId: menuItem.brand?.id,
                            category: Category.deliveryGateFlow,
                            dispensaryId: dispo.id,
                            label: "product card",
                            menuItemId: menuItem.menuItemId,
                            merchandisingCampaignId:
                              getMerchandisingCampaignId(dispo),
                            productId: menuItem.product?.id,
                            strainId: menuItem.strain?.id,
                          })
                        }
                        menuCardIndicators={getMenuItemIndicator(menuItem)}
                        menuItem={menuItem}
                        showFulfillment={false}
                      >
                        <MenuItemCardActions
                          menuItem={menuItem}
                          merchandisingCampaignId={getMerchandisingCampaignId(
                            dispo,
                          )}
                          onReservation={() => {
                            const isDispensaryFiltered = selectFilterValues(
                              MenuItemFilterSection.DispensaryName,
                            ).includes(`${menuItem.dispensary.name}`);
                            if (!isDispensaryFiltered) {
                              addSnackBarItem(
                                SnackBarMessages.resultsFilteredToDispensary,
                              );
                              toggleFilter({
                                [`${MenuItemFilterSection.DispensaryName}[]`]:
                                  menuItem.dispensary.name,
                              });
                            }
                          }}
                        />
                      </MenuItemCard>
                    </TrackImpression>
                  ))
                : ["a", "b", "c"].map((n) => (
                    <MenuCardSkeleton key={n} className="shadow-low rounded" />
                  ))}
            </Carousel>
          </TrackImpression>
        ))}
      {dispensaries.length > visibleDispensaries.length && (
        <TrackImpression
          trackingFunction={() =>
            publishEvent({
              action: Action.impression,
              category: Category.deliveryGateFlow,
              label: "product card",
            })
          }
        >
          <Button
            variant="secondary"
            className="mt-xl mb-xxl"
            onClick={loadMoreDispos}
          >
            show more results
          </Button>
        </TrackImpression>
      )}
    </div>
  );
};

export default DeliveryCarousels;
